import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Brain, Sparkles, TrendingUp, AlertCircle, Zap, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LandingNav from '../components/Landing-Nav.js';
import FooterLanding from '../components/FooterLanding.js';

const AIFeature = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Brain,
      title: 'AI Schedule Optimization',
      description: 'Machine learning analyzes historical data, employee preferences, and business patterns to create optimal schedules automatically.'
    },
    {
      icon: TrendingUp,
      title: 'Demand Forecasting',
      description: 'Predict staffing needs based on sales data, weather, events, and historical trends. Never be overstaffed or understaffed again.'
    },
    {
      icon: Sparkles,
      title: 'Smart Recommendations',
      description: 'AI suggests schedule adjustments, identifies inefficiencies, and recommends best practices specific to your business.'
    },
    {
      icon: AlertCircle,
      title: 'Anomaly Detection',
      description: 'Automatically flag unusual patterns, potential time theft, scheduling conflicts, and compliance risks before they become problems.'
    },
    {
      icon: Zap,
      title: 'Automated Workflows',
      description: 'Intelligent automation handles routine tasks like shift reminders, absence notifications, and timesheet approvals without human intervention.'
    },
    {
      icon: CheckCircle2,
      title: 'Predictive Analytics',
      description: 'Forecast turnover risks, identify training needs, predict overtime costs, and get ahead of workforce challenges.'
    }
  ];

  const benefits = [
    'Reduce scheduling time from hours to minutes with AI',
    '35% reduction in Employee costs through optimization',
    'Predict staffing needs with 95% accuracy',
    'Automatic conflict resolution and compliance checking',
    'Learn from your data and improve over time',
    'Natural language queries for instant insights',
    'Proactive alerts for potential issues',
    'Employee preference learning and matching',
    'Continuous optimization based on results',
    'Integration with external data sources (weather, events)'
  ];

  const useCases = [
    {
      title: 'Retail Demand Planning',
      description: 'AI analyzes foot traffic patterns, sales data, promotional calendars, weather forecasts, and local events to predict exactly how many employees you need at any given time. Optimize for sales while minimizing Employee waste.'
    },
    {
      title: 'Healthcare Staffing',
      description: 'Predict patient volume, account for seasonality, track certification requirements, and ensure proper staff mix automatically. AI considers union rules, seniority, preferences, and regulatory requirements simultaneously.'
    },
    {
      title: 'Restaurant Optimization',
      description: 'Forecast covers based on reservations, weather, events, and historical data. AI schedules the perfect mix of front-of-house and back-of-house staff, accounting for prep needs and anticipated rushes.'
    },
    {
      title: 'Call Center Efficiency',
      description: 'AI predicts call volume by hour, day, and season. Schedule agents to match demand patterns perfectly. Identify who handles what call types best and route accordingly. Minimize wait times while controlling costs.'
    }
  ];

  return (
    <>
      <LandingNav />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-violet-600 to-fuchsia-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              AI-Powered Intelligence
            </h1>
            <p className="text-xl md:text-2xl text-violet-100 mb-8 max-w-4xl mx-auto">
              Harness the power of artificial intelligence to optimize schedules, predict staffing needs, and reduce Employee costs by 35% while improving employee satisfaction.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/signup')}
                className="bg-white text-violet-600 px-8 py-4 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all flex items-center justify-center"
              >
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </button>
              <button 
                onClick={() => navigate('/contact')}
                className="bg-violet-500/20 border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-violet-500/30 transition-all"
              >
                Schedule Demo
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Screenshot Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-200">
            <img 
              src="/Ai page.png" 
              alt="AI-Powered Intelligence Interface"
              className="w-full h-auto rounded-xl"
            />
          </div>
          <div className="grid grid-cols-3 gap-6 mt-8">
            <div className="bg-white rounded-xl p-6 shadow text-center">
              <div className="text-4xl font-bold text-violet-600 mb-2">35%</div>
              <div className="text-gray-600">Cost Reduction</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow text-center">
              <div className="text-4xl font-bold text-fuchsia-600 mb-2">95%</div>
              <div className="text-gray-600">Forecast Accuracy</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">90%</div>
              <div className="text-gray-600">Time Saved</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
            Next-Generation AI Features
          </h2>
          <p className="text-xl text-center text-gray-600 mb-16 max-w-3xl mx-auto">
            Machine learning and artificial intelligence working 24/7 to optimize your workforce.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-violet-50 to-fuchsia-50 rounded-2xl p-8 border border-violet-100 hover:shadow-xl transition-all"
                >
                  <div className="w-14 h-14 bg-violet-600 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                The Future of Workforce Management
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Stop relying on guesswork and manual processes. Let AI analyze millions of data points to find the perfect schedule every time.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle2 className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">How AI Works</h3>
              <div className="space-y-6">
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-violet-600 rounded-full flex items-center justify-center text-white font-bold mr-4">1</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Data Collection</h4>
                    <p className="text-gray-600">AI gathers historical schedules, sales data, attendance records, and external factors.</p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-violet-600 rounded-full flex items-center justify-center text-white font-bold mr-4">2</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Pattern Recognition</h4>
                    <p className="text-gray-600">Machine learning identifies patterns, trends, and correlations humans might miss.</p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-violet-600 rounded-full flex items-center justify-center text-white font-bold mr-4">3</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Optimization</h4>
                    <p className="text-gray-600">AI generates optimal schedules and recommendations based on your specific goals.</p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-violet-600 rounded-full flex items-center justify-center text-white font-bold mr-4">4</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Continuous Learning</h4>
                    <p className="text-gray-600">System learns from outcomes and improves predictions over time automatically.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
            AI Delivering Results Across Industries
          </h2>
          <p className="text-xl text-center text-gray-600 mb-16 max-w-3xl mx-auto">
            See how artificial intelligence transforms workforce management in your industry.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-gray-50 to-violet-50 rounded-2xl p-8 border border-gray-200 hover:shadow-xl transition-all"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{useCase.title}</h3>
                <p className="text-gray-600 leading-relaxed">{useCase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-violet-600 to-fuchsia-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Experience the Power of AI
          </h2>
          <p className="text-xl text-violet-100 mb-8">
            Join innovative companies reducing Employee costs by 35% with AI-powered workforce management.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/signup')}
              className="bg-white text-violet-600 px-8 py-4 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all"
            >
              Start Free 14-Day Trial
            </button>
            <button 
              onClick={() => navigate('/contact')}
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/10 transition-all"
            >
              Schedule a Demo
            </button>
          </div>
        </div>
      </section>

      <FooterLanding />
    </>
  );
};

export default AIFeature;
